import { debounced } from "@/utils/debounce"
import { updatePhoto, updatePhotos } from "@/store/photo/photos-state"
import { PhotoId } from "@/store/undoable-state"
import { loadingStateAtom, showUnsupportedTooltip } from "@/store/app-state"
import { store } from "@/store/store"
import { PubSub } from "@/utils/pub-sub"
import { PhotoProcessingStatus, TaskRecord } from "@/lib/catalog"
import { isAbortError, isUnsupportedFileError } from "@/utils/error"
import { logPhotoError } from "../photo/photo-error"
import { Lock } from "@/utils/lock"
import {
	getVisibleThumbsProcessingPriorities,
	photosProcessingPriorityAtom,
} from "./processing-queue-priority"
import { UniquePhotoId } from "../previews/previews-state"
import { Sentry } from "@/telemetry/sentry"
import { holdDisplayWakeLock } from "@/utils/display-wake-lock"
import { subscribeSystemSleepEvents } from "@/utils/system-sleep-events"

type TaskPurpose = "apply" | "calculate" | "wait"
interface ProcessingTask {
	handler: () => Promise<unknown>
	purpose: TaskPurpose
	taskRecord?: TaskRecord
}

type TaskEventType = "processing" | "processed" | "canceled" | "failed"
interface TaskEvent {
	key: PhotoId
	type: TaskEventType
	purpose: TaskPurpose
	reason?: any
}

type QueueEvent = "startQueue" | "clearQueue" | "updateQueue"

const processingQueueLock = new Lock()

class ProcessingQueue {
	public tasks: Map<PhotoId, ProcessingTask>
	public queueEvents = new PubSub<QueueEvent>()
	public taskEvents = new PubSub<TaskEvent>()
	public remainingTasks = 0
	public totalTasks = 0
	public currentTask: PhotoId | null = null
	// Tracks the task currently executing. Needed because we remove a task from
	// `tasks` right before running it, which creates a brief window where queue
	// dedupe checks cannot see in-flight work by looking at `tasks` alone.
	private currentProcessingTask: (ProcessingTask & { key: PhotoId }) | null = null
	private taskPriorities: Map<PhotoId, number>
	private taskOrder: PhotoId[]
	private pauseResolvers: Array<(value: unknown) => void> = []
	private wakeLockRelease: (() => void) | null = null
	private preventDisplaySleep: boolean
	private pauseReasons: Set<string>

	public constructor(options?: { preventDisplaySleep?: boolean }) {
		this.tasks = new Map()
		this.taskPriorities = new Map()
		this.taskOrder = []
		this.preventDisplaySleep = Boolean(options?.preventDisplaySleep)
		this.pauseReasons = new Set()

		if (this.preventDisplaySleep) {
			subscribeSystemSleepEvents({
				onPause: () => {
					console.log("[SleepResume][ProcessingQueue] System pause received")
					void this.pauseQueueForReason("system-sleep")
				},
				onResume: () => {
					console.log("[SleepResume][ProcessingQueue] System resume received")
					this.resumeQueueForReason("system-sleep")
				},
			})
		}
	}

	public setTaskPriorities(priorities: Map<PhotoId, number>) {
		this.taskPriorities = priorities
		this.orderTasksDebounced()
	}

	public isProcessing() {
		return this.currentTask !== null
	}

	public async pauseQueue(options: { waitForInFlight?: boolean } = {}) {
		if (!this.isProcessing()) {
			//If not processing, no need to pause
			return
		}

		if (!options.waitForInFlight && this.tasks.size === 0) {
			return
		}

		// Queue will be paused when processing next task
		return new Promise((resolve) => {
			this.pauseResolvers.push(resolve)
		})
	}

	private resolvePauseResolvers() {
		for (const resolve of this.pauseResolvers) {
			resolve(true)
		}
		this.pauseResolvers = []
	}

	private pauseAtTaskBoundary() {
		this.resolvePauseResolvers()
		this.currentTask = null
		this.releaseWakeLock()
	}

	public resumeQueue() {
		if (this.pauseReasons.size > 0) {
			return
		}

		if (this.isProcessing()) {
			return
		}

		if (this.tasks.size === 0) {
			return
		}
		void this.processNextTask()
	}

	public async pauseQueueForReason(reason: string, options: { waitForInFlight?: boolean } = {}) {
		this.pauseReasons.add(reason)
		console.log(
			`[SleepResume][ProcessingQueue] pauseQueueForReason(${reason}); active reasons: ${Array.from(this.pauseReasons).join(",")}`,
		)
		await this.pauseQueue(options)
	}

	public resumeQueueForReason(reason: string) {
		this.pauseReasons.delete(reason)
		console.log(
			`[SleepResume][ProcessingQueue] resumeQueueForReason(${reason}); active reasons: ${Array.from(this.pauseReasons).join(",")}`,
		)
		if (this.pauseReasons.size === 0 && this.pauseResolvers.length > 0 && this.isProcessing()) {
			this.resolvePauseResolvers()
		}
		this.resumeQueue()
	}

	@debounced()
	public startQueue() {
		this.orderTasks()

		this.totalTasks = this.tasks.size
		this.remainingTasks = this.tasks.size

		this.queueEvents.publish("startQueue")

		if (!this.isProcessing()) {
			void this.processNextTask()
		}
	}

	public addTask(key: UniquePhotoId, task: ProcessingTask) {
		this.tasks.set(key, {
			purpose: task.purpose,
			taskRecord: task.taskRecord,
			handler: async () => {
				if (task.purpose === "apply") {
					this.taskEvents.publish({ key, type: "processing", purpose: task.purpose })
				}

				try {
					await task.handler()
					this.taskEvents.publish({ key, type: "processed", purpose: task.purpose })
				} catch (e) {
					if (isAbortError(e)) {
						// Canceled.
						this.taskEvents.publish({
							key,
							type: "canceled",
							reason: e,
							purpose: task.purpose,
						})
						console.warn(`Processing task canceled ${key}`, e)
					} else {
						this.taskEvents.publish({
							key,
							type: "failed",
							reason: e,
							purpose: task.purpose,
						})
						if (isUnsupportedFileError(e)) {
							updatePhoto(key, { isUnsupported: true })
							showUnsupportedTooltip()
							logPhotoError(e, {
								photoId: key,
								source: "processing",
								logAnalytics: true,
							})
						} else {
							logPhotoError(e, {
								photoId: key,
								source: "processing",
								logAnalytics: false,
							})
						}
						console.error(`Error processing photo ${key}`, e)
					}
				}
			},
		})

		this.startQueue()
	}

	public hasTask(
		key: PhotoId,
		matcher?: (task: Pick<ProcessingTask, "purpose" | "taskRecord">) => boolean,
	) {
		// Check queued tasks first.
		const queuedTask = this.tasks.get(key)
		if (queuedTask && (matcher == null || matcher(queuedTask))) {
			return true
		}

		// Then check the currently running task to avoid enqueueing duplicates
		// while the task has been popped from `tasks` but is still executing.
		const processingTask = this.currentProcessingTask
		if (processingTask?.key === key && (matcher == null || matcher(processingTask))) {
			return true
		}

		return false
	}

	public removeTask(key: PhotoId, purpose?: TaskPurpose) {
		const task = this.tasks.get(key)

		if (!task) {
			return
		}

		if (purpose && task.purpose !== purpose) {
			return
		}

		this.taskEvents.publish({ key, type: "canceled", purpose: task.purpose })

		this.tasks.delete(key)
		this.orderTasks()
	}

	public clearTasks() {
		this.queueEvents.publish("clearQueue")

		this.tasks.clear()
		this.orderTasks()
		if (!this.isProcessing()) {
			this.releaseWakeLock()
		}
	}

	private ensureWakeLock() {
		if (!this.preventDisplaySleep || this.wakeLockRelease) {
			return
		}

		this.wakeLockRelease = holdDisplayWakeLock()
	}

	private releaseWakeLock() {
		if (!this.wakeLockRelease) {
			return
		}

		this.wakeLockRelease()
		this.wakeLockRelease = null
	}

	private getTaskPriority(key: PhotoId) {
		return this.taskPriorities.get(key) || 0
	}

	@debounced()
	private orderTasksDebounced() {
		this.orderTasks()
	}

	private orderTasks() {
		this.taskOrder = Array.from(this.tasks.keys())

		this.taskOrder.sort((a, b) => {
			return this.getTaskPriority(a) - this.getTaskPriority(b)
		})
	}

	private async processNextTask() {
		if (this.pauseResolvers.length > 0) {
			this.pauseAtTaskBoundary()
			return
		}

		if (this.pauseReasons.size > 0) {
			this.currentTask = null
			return
		}

		this.remainingTasks = this.tasks.size
		this.currentTask = this.taskOrder.pop() ?? null

		// Notify of remaining tasks
		this.queueEvents.publish("updateQueue")

		if (this.currentTask == null) {
			// We reached the end of the queue
			this.releaseWakeLock()
			return
		}

		const task = this.tasks.get(this.currentTask)

		if (!task) {
			// Task is missing or removed. Continue on to next task
			void this.processNextTask()
			return
		}

		// Remove current task from queue
		this.tasks.delete(this.currentTask)
		this.currentProcessingTask = { key: this.currentTask, ...task }

		try {
			this.ensureWakeLock()
			await processingQueueLock.lock()
			await task.handler()
		} catch (e) {
			Sentry.captureException(e)
		} finally {
			processingQueueLock.unlock()
			this.currentProcessingTask = null
			// Success or error. Go to next task
			void this.processNextTask()
		}
	}
}

// ============ SETUP QUEUE ================

const previewQueue = new ProcessingQueue()
const processingQueue = new ProcessingQueue({ preventDisplaySleep: true })

export function setupProcessingQueueSubscribers() {
	store.sub(photosProcessingPriorityAtom, () => {
		const priorities = getVisibleThumbsProcessingPriorities()
		processingQueue.setTaskPriorities(priorities)
		previewQueue.setTaskPriorities(priorities)
	})

	const displayQueueStatus = () => {
		const { totalTasks, remainingTasks } = processingQueue

		if (!remainingTasks) {
			store.set(loadingStateAtom, { status: null })
		} else {
			store.set(loadingStateAtom, {
				status: "batchApplying",
				progress: totalTasks - remainingTasks,
				total: totalTasks,
			})
		}
	}

	const updatePhotosStatusAndTask = (status: PhotoProcessingStatus) => {
		// Batch update photos status for performance reasons.
		updatePhotos((changes) => {
			processingQueue.tasks.forEach((task, photoId) => {
				if (task.purpose === "apply") {
					changes[photoId] = { status, currentTask: task.taskRecord }
				}
			})
		})
	}

	processingQueue.queueEvents.subscribe((event) => {
		switch (event) {
			case "updateQueue":
				displayQueueStatus()
				break
			case "clearQueue":
				updatePhotosStatusAndTask("none")
				break
			case "startQueue":
				updatePhotosStatusAndTask("pending")
				break
		}
	})

	processingQueue.taskEvents.subscribe(({ key, type, purpose, reason }) => {
		if (purpose === "calculate" || purpose === "wait") return //No need to update photo status if its just calculating

		switch (type) {
			case "processing":
				updatePhoto(key, { status: "applying" })
				break
			case "failed":
				updatePhoto(key, { status: "error", error: reason, currentTask: null })
				break
			case "processed":
				updatePhoto(key, { status: "none", error: null, currentTask: null })
				break
			case "canceled":
				updatePhoto(key, { status: "none", currentTask: null }) //TODO: maybe revert to error if previously has error?
				break
		}
	})
}

export { processingQueue, previewQueue }
